import React from 'react';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';
import { toast } from 'react-hot-toast';
import { 
  XMarkIcon, 
  ClipboardDocumentListIcon,
  UserIcon,
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  WrenchScrewdriverIcon,
  CogIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  TruckIcon,
  PencilSquareIcon,
  TrashIcon,
  PrinterIcon,
  DocumentArrowDownIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { useCustomers } from '../../hooks/useCustomers';
import { useWorksheets } from '../../hooks/useWorksheets';
import { Worksheet, WorksheetItem, WorksheetTask, WorksheetStatus, WORKSHEET_STATUSES } from '../../types/worksheet';
import { Button } from '../common/Button';
import Modal from '../common/Modal';
import DigitalSignature from '../common/DigitalSignature';
import WorksheetItems from './WorksheetItems';
import TaskChecklist from './TaskChecklist';
import TimeTracker from './TimeTracker';
import WorksheetNotes from './WorksheetNotes';
import { timeTrackingService } from '../../lib/services/timeTrackingService';
import { generateWorksheetPdf } from '../../lib/worksheetPdfGenerator';
import { TimeEntry } from '../../types/worksheet';

interface WorksheetViewProps {
  worksheet: Worksheet;
  isOpen: boolean;
  onClose: () => void;
  onEdit?: (worksheet: Worksheet) => void;
}

const WorksheetView: React.FC<WorksheetViewProps> = ({
  worksheet: initialWorksheet,
  isOpen,
  onClose,
  onEdit
}) => {
  const { t } = useLanguage();
  const { customers } = useCustomers();
  const { addSignature, removeSignature, worksheets, updateItems, updateTasks, toggleTask, addNote, removeNote, changeStatus } = useWorksheets();
  const [isSignatureModalOpen, setIsSignatureModalOpen] = React.useState(false);
  const [timeEntries, setTimeEntries] = React.useState<TimeEntry[]>([]);
  const [timeEntriesLoading, setTimeEntriesLoading] = React.useState(false);
  
  // Use real-time data from the hook instead of static prop
  const worksheet = worksheets.find(w => w.id === initialWorksheet.id) || initialWorksheet;

  // Load time entries
  React.useEffect(() => {
    if (!isOpen || !worksheet.id) return;
    setTimeEntriesLoading(true);
    timeTrackingService.getByWorksheet(worksheet.id).then(entries => {
      setTimeEntries(entries);
      setTimeEntriesLoading(false);
    }).catch(() => setTimeEntriesLoading(false));
  }, [isOpen, worksheet.id, worksheet.updatedAt]);

  const handleItemsChange = (items: WorksheetItem[]) => {
    updateItems(worksheet.id, items);
  };

  const handleTasksChange = (tasks: WorksheetTask[]) => {
    updateTasks(worksheet.id, tasks);
  };

  const handleToggleTask = (taskId: string) => {
    toggleTask(worksheet.id, taskId);
  };

  const handleAddNote = (text: string, visibility: 'customer' | 'internal') => {
    addNote(worksheet.id, text, visibility);
  };

  const handleRemoveNote = (noteId: string) => {
    removeNote(worksheet.id, noteId);
  };

  const handleTimeEntriesChange = () => {
    timeTrackingService.getByWorksheet(worksheet.id).then(setTimeEntries);
  };

  const handleStatusChange = (newStatus: WorksheetStatus) => {
    changeStatus(worksheet.id, newStatus);
  };

  const formatDate = (date: any) => {
    if (!date) return '';
    
    try {
      if (date && typeof date.toDate === 'function') {
        return format(date.toDate(), 'yyyy. MM. dd.', { locale: hu });
      }
      
      if (date instanceof Date && !isNaN(date.getTime())) {
        return format(date, 'yyyy. MM. dd.', { locale: hu });
      }
      
      if (typeof date === 'string') {
        const parsedDate = new Date(date);
        if (!isNaN(parsedDate.getTime())) {
          return format(parsedDate, 'yyyy. MM. dd.', { locale: hu });
        }
      }
      
      return '';
    } catch (error) {
      return '';
    }
  };

  const formatDateTime = (date: any) => {
    if (!date) return '';
    
    try {
      if (date && typeof date.toDate === 'function') {
        return format(date.toDate(), 'yyyy. MM. dd. HH:mm:ss', { locale: hu });
      }
      
      if (date instanceof Date && !isNaN(date.getTime())) {
        return format(date, 'yyyy. MM. dd. HH:mm:ss', { locale: hu });
      }
      
      if (typeof date === 'string') {
        const parsedDate = new Date(date);
        if (!isNaN(parsedDate.getTime())) {
          return format(parsedDate, 'yyyy. MM. dd. HH:mm:ss', { locale: hu });
        }
      }
      
      return '';
    } catch (error) {
      return '';
    }
  };

  const formatTime = (time: string | undefined) => {
    if (!time) return '';
    return time;
  };

  const getCustomerName = (customerId: string) => {
    const customer = customers.find(c => c.id === customerId);
    if (!customer) return t('worksheets.fields.unknownCustomer');
    
    return customer.type === 'organization' 
      ? customer.companyName 
      : `${customer.firstName} ${customer.lastName}`;
  };

  const getStatusColor = (status: string) => {
    const map: Record<string, string> = {
      draft: 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-500',
      assigned: 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-700',
      in_progress: 'bg-amber-100 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-700',
      review_pending: 'bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-300 border-purple-200 dark:border-purple-700',
      completed: 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700',
      sent: 'bg-cyan-100 dark:bg-cyan-900/20 text-cyan-800 dark:text-cyan-300 border-cyan-200 dark:border-cyan-700',
      partially_invoiced: 'bg-orange-100 dark:bg-orange-900/20 text-orange-800 dark:text-orange-300 border-orange-200 dark:border-orange-700',
      invoiced: 'bg-emerald-100 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700',
      cancelled: 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700',
      hibernated: 'bg-slate-100 dark:bg-slate-900/20 text-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700',
    };
    return map[status] || 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-500';
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700';
      case 'medium': return 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-700';
      case 'low': return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700';
      default: return 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-500';
    }
  };

  const getWorkTypeIcon = (type: string) => {
    switch (type) {
      case 'installation': return <WrenchScrewdriverIcon className="h-4 w-4" />;
      case 'maintenance': return <CogIcon className="h-4 w-4" />;
      case 'repair': return <WrenchScrewdriverIcon className="h-4 w-4" />;
      case 'inspection': return <MagnifyingGlassIcon className="h-4 w-4" />;
      default: return <ClipboardDocumentListIcon className="h-4 w-4" />;
    }
  };

  const getWorkTypeColor = (type: string) => {
    switch (type) {
      case 'installation': return 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-700';
      case 'maintenance': return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700';
      case 'repair': return 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700';
      case 'inspection': return 'bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-300 border-purple-200 dark:border-purple-700';
      default: return 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-500';
    }
  };

  const getStatusColorHex = (status: string) => {
    const map: Record<string, string> = {
      draft: '#6b7280',
      assigned: '#2563eb',
      in_progress: '#d97706',
      review_pending: '#7c3aed',
      completed: '#059669',
      sent: '#0891b2',
      partially_invoiced: '#ea580c',
      invoiced: '#059669',
      cancelled: '#dc2626',
      hibernated: '#475569',
    };
    return map[status] || '#6b7280';
  };

  const getPriorityColorHex = (priority: string) => {
    switch (priority) {
      case 'high': return '#dc2626';
      case 'medium': return '#d97706';
      case 'low': return '#059669';
      default: return '#6b7280';
    }
  };

  const getWorkTypeColorHex = (type: string) => {
    switch (type) {
      case 'installation': return '#2563eb';
      case 'maintenance': return '#059669';
      case 'repair': return '#dc2626';
      case 'inspection': return '#7c3aed';
      case 'other': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const handleSignature = async (signatureData: string, signerName: string, signerRole: string) => {
    try {
      // Get current location (optional)
      let location = 'Unknown location';
      if (navigator.geolocation) {
        try {
          const position = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
          });
          location = `${position.coords.latitude.toFixed(6)}, ${position.coords.longitude.toFixed(6)}`;
        } catch (geoError) {
          console.warn('Could not get location:', geoError);
        }
      }

      // Create new signature object
      const newSignature = {
        signedBy: 'current-user', // This should come from auth context
        signerName,
        signerRole,
        signatureData,
        signedAt: new Date(),
        location,
        deviceInfo: navigator.userAgent
      };

      // Add signature to worksheet in database
      await addSignature(worksheet.id, newSignature);
      
      toast.success(t('worksheets.messages.signatureAdded'));
      setIsSignatureModalOpen(false);
      
    } catch (error) {
      console.error('Error adding signature:', error);
      toast.error(t('worksheets.messages.signatureError'));
    }
  };

  const handleRemoveSignature = async (signatureId: string) => {
    try {
      await removeSignature(worksheet.id, signatureId);
      toast.success(t('worksheets.messages.signatureRemoved'));
    } catch (error) {
      console.error('Error removing signature:', error);
      toast.error(t('worksheets.messages.signatureRemoveError'));
    }
  };

  const handlePrint = () => {
    // Create print content
    const printContent = `
      <!DOCTYPE html>
      <html lang="hu">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>${t('worksheets.title')} - ${worksheet.worksheetNumber}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: 'Segoe UI', Arial, sans-serif;
              font-size: 11px;
              line-height: 1.2;
              color: #333;
              background: white;
              padding: 15px;
            }
            
            .print-container {
              max-width: 100%;
              margin: 0;
            }
            
            .header {
              text-align: center;
              margin-bottom: 15px;
              padding-bottom: 10px;
              border-bottom: 1px solid #e5e7eb;
            }
            
            .header h1 {
              font-size: 22px;
              color: #1e40af;
              margin-bottom: 5px;
              font-weight: bold;
            }
            
            .header .subtitle {
              font-size: 12px;
              color: #6b7280;
              margin-bottom: 8px;
            }
            
            .worksheet-number {
              font-size: 14px;
              font-weight: bold;
              color: #059669;
              background: #ecfdf5;
              padding: 4px 12px;
              border-radius: 6px;
              display: inline-block;
            }
            
            .section {
              margin-bottom: 12px;
              break-inside: avoid;
            }
            
            .section-header {
              font-size: 13px;
              font-weight: bold;
              color: #1e40af;
              margin-bottom: 6px;
              padding-bottom: 2px;
              border-bottom: 1px solid #e5e7eb;
            }
            
            .info-grid {
              display: grid;
              grid-template-columns: 1fr 1fr 1fr;
              gap: 8px;
              margin-bottom: 8px;
            }
            
            .info-item {
              display: flex;
              flex-direction: column;
              gap: 2px;
            }
            
            .info-label {
              font-size: 9px;
              color: #6b7280;
              font-weight: 500;
              text-transform: uppercase;
              letter-spacing: 0.3px;
            }
            
            .info-value {
              font-size: 11px;
              color: #1f2937;
              font-weight: 500;
            }
            
            .status-badge {
              display: inline-block;
              padding: 2px 8px;
              border-radius: 12px;
              font-size: 9px;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.3px;
            }
            
            .status-draft { background: #f3f4f6; color: #374151; }
            .status-assigned { background: #dbeafe; color: #1e40af; }
            .status-in_progress { background: #fef3c7; color: #d97706; }
            .status-review_pending { background: #ede9fe; color: #7c3aed; }
            .status-completed { background: #d1fae5; color: #059669; }
            .status-sent { background: #cffafe; color: #0891b2; }
            .status-partially_invoiced { background: #ffedd5; color: #ea580c; }
            .status-invoiced { background: #d1fae5; color: #059669; }
            .status-cancelled { background: #fee2e2; color: #dc2626; }
            .status-hibernated { background: #e2e8f0; color: #475569; }
            
            .priority-high { background: #fee2e2; color: #dc2626; }
            .priority-medium { background: #fef3c7; color: #d97706; }
            .priority-low { background: #d1fae5; color: #059669; }
            
            .type-installation { background: #dbeafe; color: #1e40af; }
            .type-maintenance { background: #d1fae5; color: #059669; }
            .type-repair { background: #fee2e2; color: #dc2626; }
            .type-inspection { background: #ede9fe; color: #7c3aed; }
            
            .description-box {
              background: #f9fafb;
              padding: 8px;
              border-radius: 4px;
              border: 1px solid #e5e7eb;
              margin-top: 5px;
              font-size: 11px;
              line-height: 1.3;
            }
            
            .signatures-section {
              margin-top: 15px;
            }
            
            .signature-item {
              border: 1px solid #e5e7eb;
              border-radius: 6px;
              padding: 8px;
              margin-bottom: 8px;
              background: #fafafa;
              display: flex;
              align-items: center;
              gap: 12px;
            }
            
            .signature-info {
              flex-grow: 1;
              font-size: 11px;
            }
            
            .signature-name {
              font-weight: bold;
              color: #1e40af;
              display: block;
            }
            
            .signature-role {
              color: #6b7280;
              display: block;
              font-size: 10px;
            }
            
            .signature-image {
              width: 120px;
              height: 40px;
              border: 1px solid #e5e7eb;
              border-radius: 3px;
              background: white;
              flex-shrink: 0;
            }
            
            .signature-date {
              font-size: 9px;
              color: #6b7280;
              display: block;
            }
            
            .footer {
              margin-top: 15px;
              padding-top: 10px;
              border-top: 1px solid #e5e7eb;
              display: flex;
              justify-content: space-between;
              align-items: center;
              font-size: 9px;
              color: #6b7280;
            }
            
            .footer .brand {
              font-weight: bold;
              color: #1e40af;
            }
            
            .items-table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 6px;
              font-size: 10px;
            }
            
            .items-table th {
              background: #f3f4f6;
              padding: 4px 6px;
              text-align: left;
              border: 1px solid #e5e7eb;
              font-weight: 600;
              font-size: 9px;
              color: #6b7280;
              text-transform: uppercase;
            }
            
            .items-table td {
              padding: 4px 6px;
              border: 1px solid #e5e7eb;
              vertical-align: top;
            }
            
            .items-table .item-header-row td {
              background: #f9fafb;
              font-weight: 600;
              color: #1e40af;
            }
            
            .items-table .text-right { text-align: right; }
            .items-table .text-center { text-align: center; }
            
            .items-total-row td {
              background: #f0f9ff;
              font-weight: 700;
              border-top: 2px solid #1e40af;
            }
            
            .task-list {
              list-style: none;
              margin-top: 6px;
            }
            
            .task-item {
              display: flex;
              align-items: center;
              gap: 6px;
              padding: 3px 0;
              border-bottom: 1px solid #f3f4f6;
              font-size: 10px;
            }
            
            .task-checkbox {
              width: 14px;
              height: 14px;
              border: 1px solid #d1d5db;
              border-radius: 2px;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              flex-shrink: 0;
            }
            
            .task-checkbox.done {
              background: #d1fae5;
              border-color: #059669;
              color: #059669;
              font-size: 10px;
            }
            
            .task-text { flex-grow: 1; }
            .task-text.done { text-decoration: line-through; color: #9ca3af; }
            
            .time-entries-table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 6px;
              font-size: 10px;
            }
            
            .time-entries-table th {
              background: #f3f4f6;
              padding: 4px 6px;
              text-align: left;
              border: 1px solid #e5e7eb;
              font-weight: 600;
              font-size: 9px;
              color: #6b7280;
            }
            
            .time-entries-table td {
              padding: 4px 6px;
              border: 1px solid #e5e7eb;
            }
            
            .notes-list {
              margin-top: 6px;
            }
            
            .note-item {
              padding: 6px 8px;
              border: 1px solid #e5e7eb;
              border-radius: 4px;
              margin-bottom: 6px;
              background: #fafafa;
              font-size: 10px;
            }
            
            .note-visibility {
              display: inline-block;
              padding: 1px 6px;
              border-radius: 8px;
              font-size: 8px;
              font-weight: 600;
              margin-bottom: 3px;
            }
            
            .note-visibility-customer { background: #dbeafe; color: #1e40af; }
            .note-visibility-internal { background: #fef3c7; color: #d97706; }
            
            .note-text { color: #1f2937; line-height: 1.3; }
            .note-meta { font-size: 8px; color: #9ca3af; margin-top: 3px; }
            
            @media print {
              body {
                print-color-adjust: exact;
                -webkit-print-color-adjust: exact;
                font-size: 10px;
                padding: 0;
              }
              
              .print-container {
                padding: 0;
              }
              
              .section {
                break-inside: avoid;
                margin-bottom: 8px;
              }
              
              .signature-item {
                break-inside: avoid;
              }
              
              .info-grid {
                grid-template-columns: 1fr 1fr 1fr 1fr;
                gap: 6px;
              }
              
              @page {
                margin: 12mm;
                size: A4;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            <!-- Header -->
            <div class="header">
              <h1>${t('worksheets.print.title')}</h1>
              <div class="subtitle">${t('worksheets.print.subtitle')}</div>
              <div class="worksheet-number">
                ${worksheet.worksheetNumber}
              </div>
            </div>
            
            <!-- Basic Info -->
            <div class="section">
              <div class="section-header">${t('worksheets.print.basicInfo')}</div>
              <div class="info-grid">
                <div class="info-item">
                  <div class="info-label">${t('worksheets.fields.title')}</div>
                  <div class="info-value">${worksheet.title}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">${t('worksheets.fields.customer')}</div>
                  <div class="info-value">${getCustomerName(worksheet.customerId)}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">${t('worksheets.fields.workType')}</div>
                  <div class="info-value">
                    <span class="status-badge type-${worksheet.workType}">
                      ${t(`worksheets.workTypes.${worksheet.workType}`)}
                    </span>
                  </div>
                </div>
                <div class="info-item">
                  <div class="info-label">${t('worksheets.fields.priority')}</div>
                  <div class="info-value">
                    <span class="status-badge priority-${worksheet.priority}">
                      ${t(`worksheets.priority.${worksheet.priority}`)}
                    </span>
                  </div>
                </div>
                <div class="info-item">
                  <div class="info-label">${t('worksheets.fields.status')}</div>
                  <div class="info-value">
                    <span class="status-badge status-${worksheet.status}">
                      ${t(`worksheets.status.${worksheet.status}`)}
                    </span>
                  </div>
                </div>
                ${worksheet.location ? `
                <div class="info-item">
                  <div class="info-label">${t('worksheets.fields.location')}</div>
                  <div class="info-value">${worksheet.location}</div>
                </div>
                ` : ''}
                ${worksheet.worksheetDate ? `
                <div class="info-item">
                  <div class="info-label">${t('worksheets.fields.worksheetDate')}</div>
                  <div class="info-value">${formatDate(worksheet.worksheetDate)}</div>
                </div>
                ` : ''}
                ${worksheet.dueDate ? `
                <div class="info-item">
                  <div class="info-label">${t('worksheets.fields.dueDate')}</div>
                  <div class="info-value">${formatDate(worksheet.dueDate)}</div>
                </div>
                ` : ''}
              </div>
            </div>
            
            <!-- Work Details -->
            ${worksheet.description || worksheet.startTime || worksheet.endTime ? `
            <div class="section">
              <div class="section-header">${t('worksheets.fields.workDetails')}</div>
              <div class="info-grid">
                ${worksheet.startTime && worksheet.endTime ? `
                <div class="info-item">
                  <div class="info-label">${t('worksheets.print.workTime')}</div>
                  <div class="info-value">${formatTime(worksheet.startTime)} - ${formatTime(worksheet.endTime)}</div>
                </div>
                ` : ''}
              </div>
              ${worksheet.description ? `
              <div class="description-box">
                <strong>${t('worksheets.fields.description')}:</strong><br>
                ${worksheet.description}
              </div>
              ` : ''}
            </div>
            ` : ''}
            
            <!-- Travel Info -->
            ${worksheet.travelKmStart || worksheet.travelKmEnd || worksheet.travelKmTotal || worksheet.travelCosts ? `
            <div class="section">
              <div class="section-header">${t('worksheets.fields.travelInfo')}</div>
              <div class="info-grid">
                ${worksheet.travelKmStart ? `
                <div class="info-item">
                  <div class="info-label">${t('worksheets.fields.travelKmStart')}</div>
                  <div class="info-value">${worksheet.travelKmStart} km</div>
                </div>
                ` : ''}
                ${worksheet.travelKmEnd ? `
                <div class="info-item">
                  <div class="info-label">${t('worksheets.fields.travelKmEnd')}</div>
                  <div class="info-value">${worksheet.travelKmEnd} km</div>
                </div>
                ` : ''}
                ${worksheet.travelKmTotal ? `
                <div class="info-item">
                  <div class="info-label">${t('worksheets.fields.travelKmTotal')}</div>
                  <div class="info-value">${worksheet.travelKmTotal} km</div>
                </div>
                ` : ''}
                ${worksheet.travelCosts ? `
                <div class="info-item">
                  <div class="info-label">${t('worksheets.fields.travelCosts')}</div>
                  <div class="info-value">${worksheet.travelCosts} Ft</div>
                </div>
                ` : ''}
              </div>
            </div>
            ` : ''}
            
            <!-- Deployment Info -->
            ${worksheet.deploymentOccurred ? `
            <div class="section">
              <div class="section-header">${t('worksheets.fields.deploymentInfo')}</div>
              <div class="info-grid">
                <div class="info-item">
                  <div class="info-label">${t('worksheets.fields.deployment')}</div>
                  <div class="info-value">
                    <span class="status-badge status-completed">${t('worksheets.fields.deploymentYes')}</span>
                  </div>
                </div>
                ${worksheet.deploymentDate ? `
                <div class="info-item">
                  <div class="info-label">${t('worksheets.fields.deploymentDate')}</div>
                  <div class="info-value">${formatDate(worksheet.deploymentDate)}</div>
                </div>
                ` : ''}
                ${worksheet.deploymentTime ? `
                <div class="info-item">
                  <div class="info-label">${t('worksheets.fields.deploymentTime')}</div>
                  <div class="info-value">${formatTime(worksheet.deploymentTime)}</div>
                </div>
                ` : ''}
              </div>
            </div>
            ` : ''}
            
            <!-- Materials & Equipment -->
            ${worksheet.materials || worksheet.equipment ? `
            <div class="section">
              <div class="section-header">${t('worksheets.fields.materialsAndEquipment')}</div>
              ${worksheet.materials ? `
              <div class="description-box">
                <strong>${t('worksheets.fields.materials')}:</strong><br>
                ${worksheet.materials}
              </div>
              ` : ''}
              ${worksheet.equipment ? `
              <div class="description-box">
                <strong>${t('worksheets.fields.equipment')}:</strong><br>
                ${worksheet.equipment}
              </div>
              ` : ''}
            </div>
            ` : ''}
            
            <!-- Items / Tételek -->
            ${worksheet.items && worksheet.items.length > 0 ? `
            <div class="section">
              <div class="section-header">${t('worksheets.items.title')}</div>
              <table class="items-table">
                <thead>
                  <tr>
                    <th style="width: 30%;">${t('worksheets.items.name')}</th>
                    <th style="width: 8%;" class="text-center">${t('worksheets.items.quantity')}</th>
                    <th style="width: 10%;" class="text-center">${t('worksheets.items.unit')}</th>
                    <th style="width: 12%;" class="text-right">${t('worksheets.items.unitPrice')}</th>
                    <th style="width: 8%;" class="text-center">${t('worksheets.items.vat')}%</th>
                    <th style="width: 12%;" class="text-right">${t('worksheets.items.netTotal')}</th>
                    <th style="width: 12%;" class="text-right">${t('worksheets.items.grossTotal')}</th>
                  </tr>
                </thead>
                <tbody>
                  ${worksheet.items.map(item => {
                    if (item.type === 'header') {
                      return '<tr class="item-header-row"><td colspan="7">' + (item.name || '') + '</td></tr>';
                    }
                    return '<tr>' +
                      '<td>' + (item.name || '') + (item.notes ? '<br><span style="color:#9ca3af;font-size:9px;">' + item.notes + '</span>' : '') + '</td>' +
                      '<td class="text-center">' + (item.quantity || 0) + '</td>' +
                      '<td class="text-center">' + (item.unit || '') + '</td>' +
                      '<td class="text-right">' + (item.unitPrice || 0).toLocaleString('hu-HU') + ' Ft</td>' +
                      '<td class="text-center">' + (item.vatRate || 0) + '%</td>' +
                      '<td class="text-right">' + (item.totalNet || 0).toLocaleString('hu-HU') + ' Ft</td>' +
                      '<td class="text-right">' + (item.totalGross || 0).toLocaleString('hu-HU') + ' Ft</td>' +
                      '</tr>';
                  }).join('')}
                  <tr class="items-total-row">
                    <td colspan="5" style="text-align: right;">${t('worksheets.items.total')}</td>
                    <td class="text-right">${(worksheet.items.reduce((sum: number, i: any) => sum + (i.totalNet || 0), 0)).toLocaleString('hu-HU')} Ft</td>
                    <td class="text-right">${(worksheet.items.reduce((sum: number, i: any) => sum + (i.totalGross || 0), 0)).toLocaleString('hu-HU')} Ft</td>
                  </tr>
                </tbody>
              </table>
            </div>
            ` : ''}
            
            <!-- Tasks / Feladatok -->
            ${worksheet.tasks && worksheet.tasks.length > 0 ? `
            <div class="section">
              <div class="section-header">${t('worksheets.tasks.title')} (${worksheet.tasks.filter((t: any) => t.completed).length}/${worksheet.tasks.length})</div>
              <ul class="task-list">
                ${worksheet.tasks.map((task: any) => `
                  <li class="task-item">
                    <span class="task-checkbox ${task.completed ? 'done' : ''}">${task.completed ? '✓' : ''}</span>
                    <span class="task-text ${task.completed ? 'done' : ''}">${task.title}${task.description ? ' — ' + task.description : ''}</span>
                    ${task.estimatedMinutes ? '<span style="color:#9ca3af;font-size:9px;">(' + task.estimatedMinutes + ' p)</span>' : ''}
                  </li>
                `).join('')}
              </ul>
            </div>
            ` : ''}
            
            <!-- Time Entries / Munkaidő -->
            ${timeEntries && timeEntries.length > 0 ? `
            <div class="section">
              <div class="section-header">${t('worksheets.timeTracking.title')}</div>
              <table class="time-entries-table">
                <thead>
                  <tr>
                    <th style="width: 18%;">${t('worksheets.timeTracking.startLabel')}</th>
                    <th style="width: 18%;">${t('worksheets.timeTracking.endLabel')}</th>
                    <th style="width: 10%;" class="text-center">${t('worksheets.timeTracking.duration')}</th>
                    <th style="width: 10%;" class="text-center">${t('worksheets.timeTracking.workersCount')}</th>
                    <th style="width: 8%;" class="text-center">${t('worksheets.timeTracking.type')}</th>
                    <th>${t('worksheets.timeTracking.description')}</th>
                  </tr>
                </thead>
                <tbody>
                  ${timeEntries.map((entry: any) => {
                    const start = entry.startTime?.toDate ? entry.startTime.toDate() : new Date(entry.startTime);
                    const end = entry.endTime?.toDate ? entry.endTime.toDate() : (entry.endTime ? new Date(entry.endTime) : null);
                    const hours = Math.floor((entry.duration || 0) / 60);
                    const mins = (entry.duration || 0) % 60;
                    return '<tr>' +
                      '<td>' + (start ? format(start, 'yyyy. MM. dd. HH:mm') : '-') + '</td>' +
                      '<td>' + (end ? format(end, 'yyyy. MM. dd. HH:mm') : '-') + '</td>' +
                      '<td class="text-center">' + (hours > 0 ? hours + 'ó ' : '') + mins + 'p</td>' +
                      '<td class="text-center">' + (entry.workerCount || 1) + '</td>' +
                      '<td class="text-center">' + (entry.isManual ? t('worksheets.timeTracking.manualType') : t('worksheets.timeTracking.timerType')) + '</td>' +
                      '<td>' + (entry.description || '') + '</td>' +
                      '</tr>';
                  }).join('')}
                  <tr class="items-total-row">
                    <td colspan="2" style="text-align: right;">${t('worksheets.items.total')}</td>
                    <td class="text-center">${Math.floor((timeEntries.reduce((s: number, e: any) => s + (e.duration || 0), 0)) / 60)}ó ${((timeEntries.reduce((s: number, e: any) => s + (e.duration || 0), 0)) % 60)}p</td>
                    <td colspan="3"></td>
                  </tr>
                </tbody>
              </table>
            </div>
            ` : ''}
            
            <!-- Notes / Megjegyzések -->
            ${worksheet.notes_list && worksheet.notes_list.length > 0 ? `
            <div class="section">
              <div class="section-header">${t('worksheets.notes.title')}</div>
              <div class="notes-list">
                ${worksheet.notes_list.map((note: any) => `
                  <div class="note-item">
                    <span class="note-visibility note-visibility-${note.visibility}">
                      ${note.visibility === 'customer' ? t('worksheets.notes.customer') : t('worksheets.notes.internal')}
                    </span>
                    <div class="note-text">${note.text}</div>
                    <div class="note-meta">${note.createdByName || ''} — ${note.createdAt?.toDate ? format(note.createdAt.toDate(), 'yyyy. MM. dd. HH:mm') : ''}</div>
                  </div>
                `).join('')}
              </div>
            </div>
            ` : ''}
            
            <!-- Safety Notes -->
            ${worksheet.safetyNotes ? `
            <div class="section">
              <div class="section-header">${t('worksheets.fields.safetyNotes')}</div>
              <div class="description-box">
                ${worksheet.safetyNotes}
              </div>
            </div>
            ` : ''}
            
            <!-- Special Notes -->
            ${worksheet.specialNotes ? `
            <div class="section">
              <div class="section-header">${t('worksheets.fields.specialNotes')}</div>
              <div class="description-box">
                ${worksheet.specialNotes}
              </div>
            </div>
            ` : ''}
            
            <!-- Signatures -->
            ${worksheet.signatures && worksheet.signatures.length > 0 ? `
            <div class="section signatures-section">
              <div class="section-header">${t('worksheets.fields.signatures')}</div>
              ${worksheet.signatures.map(signature => `
                <div class="signature-item">
                  <div class="signature-info">
                    <span class="signature-name">${signature.signerName}</span>
                    <span class="signature-role">${signature.signerRole}</span>
                    <span class="signature-date">${formatDateTime(signature.signedAt)}</span>
                  </div>
                  <img src="${signature.signatureData}" alt="${t('worksheets.fields.signatures')}" class="signature-image">
                </div>
              `).join('')}
            </div>
            ` : ''}
            
            <!-- Footer -->
            <div class="footer">
              <div>${t('worksheets.print.generatedAt')} ${format(new Date(), 'yyyy. MM. dd. HH:mm:ss', { locale: hu })}</div>
              <div class="brand">${t('worksheets.print.subtitle')}</div>
            </div>
          </div>
        </body>
      </html>
    `;

    // Open print window
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      
      // Wait for content to load then print
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
        }, 500);
      };
    } else {
      toast.error(t('worksheets.messages.printBlocked'));
    }
  };

  const handleDownloadPdf = () => {
    try {
      generateWorksheetPdf({
        worksheet,
        timeEntries,
        customerName: getCustomerName(worksheet.customerId),
        t,
      });
      toast.success(t('worksheets.messages.pdfSuccess'));
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error(t('worksheets.messages.pdfError'));
    }
  };

  return (
    <>
      <Modal 
        isOpen={isOpen} 
        onClose={onClose} 
        title={t('worksheets.view')}
        size="4xl"
      >
        <div className="p-3 sm:p-4">
          {/* Enhanced Header */}
          <div className="bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 dark:from-blue-900/20 dark:via-indigo-900/20 dark:to-purple-900/20 border border-indigo-200 dark:border-indigo-700 rounded-lg p-3 sm:p-4 mb-4 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <div className="p-1.5 sm:p-2 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg flex-shrink-0">
                  <ClipboardDocumentListIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-base sm:text-lg font-bold text-gray-800 dark:text-gray-200 truncate">{worksheet.title}</h2>
                  <p className="text-xs sm:text-sm text-indigo-600 dark:text-indigo-400 font-medium">{worksheet.worksheetNumber}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={worksheet.status}
                  onChange={(e) => handleStatusChange(e.target.value as WorksheetStatus)}
                  className="px-2 sm:px-3 py-1.5 rounded-full text-xs font-semibold border-0 shadow-sm text-white cursor-pointer outline-none"
                  style={{
                    backgroundColor: getStatusColorHex(worksheet.status)
                  }}
                >
                  {WORKSHEET_STATUSES.map(s => (
                    <option key={s.value} value={s.value} className="text-gray-800 bg-white">
                      {t(s.labelKey)}
                    </option>
                  ))}
                </select>
                <span 
                  className="px-2 sm:px-3 py-1.5 rounded-full text-xs font-semibold border-0 shadow-sm text-white"
                  style={{
                    backgroundColor: getPriorityColorHex(worksheet.priority)
                  }}
                >
                  {t(`worksheets.priority.${worksheet.priority}`)}
                </span>
              </div>
            </div>
          </div>

          {/* Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
            {/* Left Column */}
            <div className="space-y-3 sm:space-y-4">
              {/* Basic Info */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-2 sm:p-3 shadow-sm">
                <h3 className="text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
                  <DocumentTextIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-600 dark:text-gray-400" />
                  {t('worksheets.print.basicInfo')}
                </h3>
                <div className="space-y-1.5 sm:space-y-2 text-[10px] sm:text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('worksheets.fields.customer')}:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{getCustomerName(worksheet.customerId)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('worksheets.fields.location')}:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{worksheet.location || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('worksheets.fields.dueDate')}:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{formatDate(worksheet.dueDate) || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('worksheets.fields.worksheetDate')}:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{formatDate(worksheet.worksheetDate) || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Work Details */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border border-blue-200 dark:border-blue-700 rounded-lg p-2 sm:p-3 shadow-sm">
                <h3 className="text-xs sm:text-sm font-semibold text-blue-800 dark:text-blue-300 mb-2 flex items-center gap-1">
                  {getWorkTypeIcon(worksheet.workType)}
                  {t('worksheets.fields.workDetails')}
                </h3>
                <div className="space-y-1.5 sm:space-y-2 text-[10px] sm:text-xs">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-400">{t('worksheets.fields.workType')}:</span>
                    <span 
                      className="px-2 py-1 rounded-full text-xs font-medium border-0 text-white"
                      style={{
                        backgroundColor: getWorkTypeColorHex(worksheet.workType)
                      }}
                    >
                      {t(`worksheets.workTypes.${worksheet.workType}`)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('worksheets.timeTracking.duration')}:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{formatTime(worksheet.startTime)} - {formatTime(worksheet.endTime)}</span>
                  </div>
                  {worksheet.description && (
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">{t('worksheets.fields.description')}:</span>
                      <p className="font-medium mt-1 text-gray-900 dark:text-gray-100">{worksheet.description}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Travel Info */}
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border border-purple-200 dark:border-purple-700 rounded-lg p-2 sm:p-3 shadow-sm">
                <h3 className="text-xs sm:text-sm font-semibold text-purple-800 dark:text-purple-300 mb-2 flex items-center gap-1">
                  <TruckIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  {t('worksheets.fields.travelInfo')}
                </h3>
                <div className="grid grid-cols-2 gap-2 text-[10px] sm:text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('worksheets.fields.travelKmStart')}:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{worksheet.travelKmStart || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('worksheets.fields.travelKmEnd')}:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{worksheet.travelKmEnd || '-'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-400">{t('worksheets.fields.travelKmTotal')}:</span>
                    <span 
                      className="px-2 py-1 rounded-full text-xs font-medium border-0 text-white"
                      style={{
                        backgroundColor: worksheet.travelKmTotal && worksheet.travelKmTotal > 0 ? '#2563eb' : '#6b7280'
                      }}
                    >
                      {worksheet.travelKmTotal || '-'} km
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-400">{t('worksheets.fields.travelCosts')}:</span>
                    <span 
                      className="px-2 py-1 rounded-full text-xs font-medium border-0 text-white"
                      style={{
                        backgroundColor: worksheet.travelCosts && worksheet.travelCosts > 0 ? '#d97706' : '#6b7280'
                      }}
                    >
                      {worksheet.travelCosts ? `${worksheet.travelCosts} Ft` : '-'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-3 sm:space-y-4">
              {/* Deployment */}
              <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border border-green-200 dark:border-green-700 rounded-lg p-2 sm:p-3 shadow-sm">
                <h3 className="text-xs sm:text-sm font-semibold text-green-800 dark:text-green-300 mb-2 flex items-center gap-1">
                  <TruckIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  {t('worksheets.fields.deployment')}
                </h3>
                <div className="space-y-1.5 sm:space-y-2 text-[10px] sm:text-xs">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-400">{t('worksheets.fields.deploymentOccurred')}:</span>
                    <span 
                      className="px-2 py-1 rounded-full text-xs font-medium border-0 text-white"
                      style={{
                        backgroundColor: worksheet.deploymentOccurred ? '#059669' : '#6b7280'
                      }}
                    >
                      {worksheet.deploymentOccurred ? t('worksheets.tasks.completed') : '-'}
                    </span>
                  </div>
                  {worksheet.deploymentOccurred && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('worksheets.fields.deploymentDate')}:</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{formatDate(worksheet.deploymentDate) || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('worksheets.fields.deploymentTime')}:</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{formatTime(worksheet.deploymentTime) || '-'}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Materials & Equipment */}
              <div className="bg-gradient-to-br from-yellow-50 to-amber-100 dark:from-yellow-900/20 dark:to-amber-800/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-2 sm:p-3 shadow-sm">
                <h3 className="text-xs sm:text-sm font-semibold text-amber-800 dark:text-amber-300 mb-2 flex items-center gap-1">
                  <WrenchScrewdriverIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  {t('worksheets.fields.materialsAndEquipment')}
                </h3>
                <div className="space-y-1.5 sm:space-y-2 text-[10px] sm:text-xs">
                  {worksheet.materials && (
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">{t('worksheets.fields.materials')}:</span>
                      <p className="font-medium mt-1 text-gray-900 dark:text-gray-100">{worksheet.materials}</p>
                    </div>
                  )}
                  {worksheet.equipment && (
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">{t('worksheets.fields.equipment')}:</span>
                      <p className="font-medium mt-1 text-gray-900 dark:text-gray-100">{worksheet.equipment}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Signatures */}
              <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 border border-indigo-200 dark:border-indigo-700 rounded-lg p-2 sm:p-3 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs sm:text-sm font-semibold text-indigo-800 dark:text-indigo-300 flex items-center gap-1">
                    <PencilSquareIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    {t('worksheets.fields.signatures')}
                    {worksheet.signatures && worksheet.signatures.length > 0 && (
                      <span className="px-1.5 py-0.5 bg-indigo-100 dark:bg-indigo-900/20 text-indigo-800 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-700 rounded-full text-xs font-medium ml-1">
                        {worksheet.signatures.length}
                      </span>
                    )}
                  </h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsSignatureModalOpen(true)}
                    className="text-xs h-6 px-2"
                  >
                    {t('worksheets.actions.add')}
                  </Button>
                </div>
                {worksheet.signatures && worksheet.signatures.length > 0 ? (
                  <div className="space-y-2">
                    {worksheet.signatures.map((signature, index) => (
                      <div key={index} className="bg-white dark:bg-gray-700 rounded p-2 border border-gray-200 dark:border-gray-600 relative group">
                        <button
                          onClick={() => handleRemoveSignature(signature.id)}
                          className="absolute top-1 right-1 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-colors opacity-0 group-hover:opacity-100"
                          title={t('worksheets.actions.delete')}
                        >
                          <TrashIcon className="h-3 w-3" />
                        </button>
                        <div className="flex justify-between items-center text-xs pr-6">
                          <span className="font-medium text-gray-900 dark:text-gray-100">{signature.signerName}</span>
                          <span className="text-gray-500 dark:text-gray-400">{signature.signerRole}</span>
                        </div>
                        <img 
                          src={signature.signatureData} 
                          alt={t('worksheets.fields.signatures')} 
                          className="w-full h-12 object-contain bg-white dark:bg-gray-800 rounded mt-1"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {formatDateTime(signature.signedAt)}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-2">{t('worksheets.messages.noSignatures')}</p>
                )}
              </div>
            </div>
          </div>

          {/* Extended Components: Items, Tasks, Time, Notes */}
          <div className="mt-3 sm:mt-4 space-y-3 sm:space-y-4">
            {/* Worksheet Items */}
            <WorksheetItems
              items={worksheet.items || []}
              onChange={handleItemsChange}
            />

            {/* Task Checklist */}
            <TaskChecklist
              tasks={worksheet.tasks || []}
              onChange={handleTasksChange}
              onToggle={handleToggleTask}
            />

            {/* Time Tracker */}
            <TimeTracker
              worksheetId={worksheet.id}
              entries={timeEntries}
              onEntriesChange={handleTimeEntriesChange}
            />

            {/* Notes */}
            <WorksheetNotes
              notes={worksheet.notes_list || []}
              onAdd={handleAddNote}
              onRemove={handleRemoveNote}
            />
          </div>

          {/* Special Notes */}
          {worksheet.specialNotes && (
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-2 sm:p-3 mt-4 shadow-sm">
              <h3 className="text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
                <ExclamationTriangleIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-600 dark:text-gray-400" />
                {t('worksheets.fields.specialNotes')}
              </h3>
              <p className="text-[10px] sm:text-xs text-gray-900 dark:text-gray-100">{worksheet.specialNotes}</p>
            </div>
          )}

          {/* Footer */}
          <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-2 mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
            <div className="flex gap-1.5 flex-wrap">
              <Button 
                variant="outline" 
                onClick={handlePrint} 
                size="sm"
                className="bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/30 px-2 py-1 text-xs sm:px-3 sm:py-1.5 sm:text-sm"
              >
                <PrinterIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 sm:mr-1" />
                <span className="hidden sm:inline">{t('worksheets.actions.print')}</span>
              </Button>
              <Button 
                variant="outline" 
                onClick={handleDownloadPdf} 
                size="sm"
                className="bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-700 hover:bg-green-100 dark:hover:bg-green-900/30 px-2 py-1 text-xs sm:px-3 sm:py-1.5 sm:text-sm"
              >
                <DocumentArrowDownIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 sm:mr-1" />
                <span className="hidden sm:inline">{t('worksheets.actions.downloadPdf')}</span>
              </Button>
            </div>
            <div className="flex gap-1.5 justify-end">
              <Button variant="outline" onClick={onClose} size="sm"
                className="px-2 py-1 text-xs sm:px-3 sm:py-1.5 sm:text-sm"
              >
                {t('worksheets.actions.close')}
              </Button>
              {onEdit && (
                <Button onClick={() => onEdit(worksheet)} size="sm"
                  className="px-2 py-1 text-xs sm:px-3 sm:py-1.5 sm:text-sm"
                >
                  <PencilSquareIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 sm:mr-1" />
                  <span className="hidden sm:inline">{t('worksheets.actions.edit')}</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* Signature Modal */}
      {isSignatureModalOpen && (
        <DigitalSignature
          isOpen={isSignatureModalOpen}
          onClose={() => setIsSignatureModalOpen(false)}
          onSave={handleSignature}
          title={t('worksheets.actions.addSignature')}
        />
      )}
    </>
  );
};

export default WorksheetView; 